import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/RevisionContext';
import { StatisticsService } from '../services/StatisticsService';
import { ProgressStatistics } from '../types/models';
import ProgressStats from '../components/ProgressStats';

/**
 * Dashboard component - main landing page
 * Shows overview of cards due for review and progress statistics
 * Requirements: 6.1, 6.2, 5.2, 5.3, 5.5
 */
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { sections, isLoading, user, config, firestoreService } = useAppContext();
  const [dueCardsCount, setDueCardsCount] = useState<number>(0);
  const [statistics, setStatistics] = useState<ProgressStatistics | null>(null);
  const [loadingStats, setLoadingStats] = useState<boolean>(true);

  const [statisticsService] = useState(() => new StatisticsService());

  /**
   * Load due cards count and statistics when user is available
   */
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.uid) {
        setLoadingStats(false);
        return;
      }

      try {
        setLoadingStats(true);

        // Set user ID for firestore service
        firestoreService.setUserId(user.uid);

        // Load all cards and reviews for statistics
        const [allCards, allReviews] = await Promise.all([
          firestoreService.getAllCards(),
          firestoreService.getAllReviews()
        ]);

        // Calculate due cards count
        const now = new Date();
        const dueCards = allCards.filter(
          card => card.schedule.nextReviewDate <= now
        );
        setDueCardsCount(dueCards.length);

        // Calculate progress statistics
        const stats = statisticsService.calculateProgressStats(allCards, allReviews);
        setStatistics(stats);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadDashboardData();
  }, [user, firestoreService, statisticsService]);

  if (isLoading || loadingStats) {
    return (
      <div className="dashboard">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>{config.ui.systemName}</h1>
      </header>

      <div className="dashboard-content">
        {/* Due Cards Section */}
        <section className="dashboard-section">
          <h2>Cards Due for Review</h2>
          <div className="due-cards-widget">
            <div className="due-cards-count">
              <span className="count-number">{dueCardsCount}</span>
              <span className="count-label">cards due</span>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => navigate(`${config.routing.basePath}/session`)}
              disabled={dueCardsCount === 0}
            >
              Start Session
            </button>
          </div>
        </section>

        {/* Progress Statistics Section */}
        {statistics && (
          <section className="dashboard-section">
            <h2>Progress Statistics</h2>
            <ProgressStats statistics={statistics} />
          </section>
        )}

        {/* Sections List Section */}
        <section className="dashboard-section">
          <h2>Sections ({sections.length})</h2>
          {sections.length === 0 ? (
            <p className="placeholder">No sections yet. Create your first section to get started!</p>
          ) : (
            <ul className="section-list">
              {sections.map((section) => (
                <li key={section.id} className="section-item">
                  <div className="section-info">
                    <span className="section-name">{section.name}</span>
                    <span className="section-count">{section.cardCount} cards</span>
                  </div>
                  <div className="section-actions">
                    <button 
                      className="btn btn-small btn-secondary"
                      onClick={() => navigate(`${config.routing.basePath}/session/${section.id}`)}
                    >
                      Review
                    </button>
                    <button 
                      className="btn btn-small btn-accent"
                      onClick={() => navigate(`${config.routing.basePath}/session/${section.id}?mode=all`)}
                    >
                      Review All
                    </button>
                    <button 
                      className="btn btn-small"
                      onClick={() => navigate(`${config.routing.basePath}/sections/${section.id}`)}
                    >
                      Manage
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="action-buttons" style={{ marginTop: '20px' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate(`${config.routing.basePath}/sections`)}
            >
              Manage Sections
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate(`${config.routing.basePath}/migration`)}
              style={{ marginLeft: '10px' }}
            >
              Migrate Legacy Cards
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
